"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const react_query_1 = require("@tanstack/react-query");
const antd_1 = require("antd");
const lodash_1 = require("lodash");
const NewEmailTemplate_1 = __importDefault(require("./NewEmailTemplate"));
const ManageEmailTemplate_1 = __importDefault(require("./ManageEmailTemplate"));
const DomainController_1 = __importDefault(require("../DomainController"));
const emailTemplates_1 = require("../../services/emailTemplates");
const queryKeys_1 = __importDefault(require("../../constants/queryKeys"));
const ApplicationContext_1 = require("../../context/ApplicationContext");
const useDebounce_1 = __importDefault(require("../../hooks/useDebounce"));
const { Text, Paragraph } = antd_1.Typography;
const handleRecordAction = (_text, _a) => {
    var { emailTemplatesId, emailTemplateName, description } = _a, rest = __rest(_a, ["emailTemplatesId", "emailTemplateName", "description"]);
    return ((0, jsx_runtime_1.jsxs)("div", Object.assign({ style: { display: 'flex', flexDirection: 'column' } }, { children: [(0, jsx_runtime_1.jsx)(react_router_dom_1.Link, Object.assign({ to: `/emailTemplates/manage/${emailTemplatesId}`, state: Object.assign({ emailTemplatesId, emailTemplateName, description }, rest) }, { children: (0, jsx_runtime_1.jsx)(Text, Object.assign({ style: { color: 'inherit' } }, { children: emailTemplateName })) }), emailTemplatesId), (0, jsx_runtime_1.jsx)(antd_1.Tooltip, Object.assign({ placement: 'bottomLeft', title: description }, { children: (0, jsx_runtime_1.jsx)(Paragraph, Object.assign({ type: 'secondary', ellipsis: { rows: 1 }, style: { maxWidth: 300, margin: 0 } }, { children: description })) }))] })));
};
const EmailTemplates = () => {
    var _a, _b, _c, _d;
    const { tableFilters, setTableFilters } = (0, ApplicationContext_1.useApplicationContext)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const location = (0, react_router_dom_1.useLocation)();
    const [{ page, limit, filter }, setTableConfig] = (0, react_1.useState)({
        page: 1,
        limit: 10,
        filter: {},
    });
    const [searchValue, setSearchValue] = (0, react_1.useState)('');
    const debouncedSearch = (0, useDebounce_1.default)(searchValue, 300); // 300ms delay
    (0, react_1.useEffect)(() => {
        var _a, _b, _c, _d;
        if (Object.keys((_a = tableFilters === null || tableFilters === void 0 ? void 0 : tableFilters.emailTemplates) !== null && _a !== void 0 ? _a : {}).length > 0) {
            setTableConfig({ page, limit, filter: (_b = tableFilters === null || tableFilters === void 0 ? void 0 : tableFilters.emailTemplates) === null || _b === void 0 ? void 0 : _b.filters });
            setSearchValue((_d = (_c = tableFilters === null || tableFilters === void 0 ? void 0 : tableFilters.emailTemplates) === null || _c === void 0 ? void 0 : _c.search) !== null && _d !== void 0 ? _d : '');
        }
    }, [tableFilters]);
    const { isLoading, isRefetching, data } = (0, react_query_1.useQuery)([queryKeys_1.default.EMAIL_TEMPLATES.ALL, page, limit, debouncedSearch, filter], (0, emailTemplates_1.getEmailTemplates)({ page, limit, search: debouncedSearch, filter }), {
        keepPreviousData: true,
        onError: (err) => {
            const { status, data: { msg } } = err.response;
            switch (status) {
                case 404: return navigate('/emailTemplates/404', { state: { comingFrom: '' } });
                case 500: return navigate('/emailTemplates/500');
                default: antd_1.message.error(msg);
            }
        },
    });
    const handleTableChange = (pagination, filters, _sorter) => {
        const { current, pageSize } = pagination;
        // remove filter keys having null value
        const modifiedFilters = Object.entries(filters)
            .filter(([_key, value]) => value !== null)
            .reduce((acc, curr) => {
            const [key, value] = curr;
            acc[key] = value;
            return acc;
        }, {});
        setTableConfig({
            page: current,
            limit: pageSize,
            filter: modifiedFilters,
        });
        setTableFilters(Object.assign(Object.assign({}, tableFilters), { emailTemplates: Object.assign(Object.assign({}, tableFilters.emailTemplates), { filters: modifiedFilters }) }));
    };
    const handleSearch = (value) => {
        setSearchValue(value);
        setTableFilters(Object.assign(Object.assign({}, tableFilters), { emailTemplates: Object.assign(Object.assign({}, tableFilters.emailTemplates), { search: value }) }));
        setTableConfig({
            page: 1,
            limit: 10,
            filter,
        });
    };
    const { data: tenantsData } = (0, react_query_1.useQuery)([queryKeys_1.default.EMAIL_TEMPLATES.TENANTS_FILTER], (0, emailTemplates_1.getEmailTemplates)({ onlyTenants: true }), { enabled: (data === null || data === void 0 ? void 0 : data.items.length) > 0 && location.pathname === `/${queryKeys_1.default.EMAIL_TEMPLATES.ALL}` });
    const { data: localesData } = (0, react_query_1.useQuery)([queryKeys_1.default.EMAIL_TEMPLATES.LOCALES_FILTER], (0, emailTemplates_1.getEmailTemplates)({ onlyLocales: true }), { enabled: (data === null || data === void 0 ? void 0 : data.items.length) > 0 && location.pathname === `/${queryKeys_1.default.EMAIL_TEMPLATES.ALL}` });
    const columns = [
        { title: 'Title', dataIndex: 'emailTemplateName', key: 'emailTemplateName', render: handleRecordAction },
        { title: 'Subject', dataIndex: 'subject', key: 'subject' },
        { title: 'Tenant', dataIndex: 'tenantName', key: 'tenantName',
            render: (_text, { tenantName }) => tenantName,
            filters: tenantsData === null || tenantsData === void 0 ? void 0 : tenantsData.map(({ tenantId, tenantName }) => ({ text: tenantName, value: tenantId })),
            filteredValue: ((_b = (_a = tableFilters.emailTemplates) === null || _a === void 0 ? void 0 : _a.filters) === null || _b === void 0 ? void 0 : _b.tenantName) || null,
        },
        { title: 'Language', dataIndex: 'localeName', key: 'localeName',
            render: (_text, { localeName }) => (0, lodash_1.capitalize)(localeName),
            filters: localesData === null || localesData === void 0 ? void 0 : localesData.map(({ code, localeName }) => ({ text: localeName, value: code })),
            filteredValue: ((_d = (_c = tableFilters.emailTemplates) === null || _c === void 0 ? void 0 : _c.filters) === null || _d === void 0 ? void 0 : _d.localeName) || null,
        },
    ];
    return ((0, jsx_runtime_1.jsx)(DomainController_1.default, { apiContext: '/emailTemplates', urlPath: '/emailTemplates', loading: isLoading || isRefetching, columns: columns, data: data, handleTableChange: handleTableChange, handleSearch: handleSearch, searchText: searchValue, title: 'Email Templates', componentForNew: (0, jsx_runtime_1.jsx)(NewEmailTemplate_1.default, {}), componentForManage: (0, jsx_runtime_1.jsx)(ManageEmailTemplate_1.default, {}), primaryKey: 'emailTemplatesId' }));
};
exports.default = EmailTemplates;
